import React, { useCallback, useEffect, useState } from "react";
import { PageHead } from "./crmUi";
import { crmApi } from "../../lib/crmApi";
import { ReplayTab, LearningTab, BriefingsTab, SandboxTab, PerformanceTab, CoachingTab } from "./InsightsTools";

const TABS = [["overview", "Overview"], ["conversion", "Conversion"], ["performance", "AI Performance"],
  ["replay", "Replay"], ["learning", "AI Learning"], ["coaching", "Coaching"],
  ["briefings", "Briefings"], ["sandbox", "AI Sandbox"], ["health", "Health"]];

export const card = "border border-[#6E1F2C]/15 bg-white p-3";
export const num = "text-xl font-semibold text-[#1C110C]";
export const lbl = "text-[9px] tracking-[0.16em] uppercase text-[#7A6B5F]";

export function Kpi({ label, value, warn, testid }) {
  return (
    <div className={`${card} ${warn && value > 0 ? "border-[#A3141A]/40" : ""}`} data-testid={testid}>
      <p className={`${num} ${warn && value > 0 ? "text-[#A3141A]" : ""}`}>{value ?? "—"}</p>
      <p className={lbl}>{label}</p>
    </div>
  );
}

export function MiniList({ title, items, render, testid }) {
  return (
    <div className={card} data-testid={testid}>
      <p className={`${lbl} mb-2`}>{title}</p>
      {(items || []).length === 0 && <p className="text-[11px] text-[#7A6B5F] italic">No data yet</p>}
      <ul className="space-y-1">
        {(items || []).map((it, i) => <li key={i} className="text-[11px] text-[#3E2A22]">{render(it)}</li>)}
      </ul>
    </div>
  );
}

function OverviewTab() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { crmApi.insightsDashboard().then(setD).catch((e) => setErr(e.message)); }, []);
  if (err) return <p className="text-xs text-[#A3141A]">{err}</p>;
  if (!d) return <p className="text-xs text-[#7A6B5F]">Loading…</p>;
  const fmt = (n) => (n || 0).toLocaleString();
  return (
    <div className="space-y-3" data-testid="insights-overview">
      <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-7 gap-2">
        <Kpi label="New leads today" value={d.today_new_leads} testid="ins-kpi-new" />
        <Kpi label="Qualified" value={d.qualified_leads} />
        <Kpi label="Hot leads" value={d.hot_leads} />
        <Kpi label="Overdue follow-ups" value={d.overdue_followups} warn />
        <Kpi label="Quotes pending" value={d.quotes_pending} warn />
        <Kpi label="Appointments today" value={d.appointments_today} />
        <Kpi label="Site visits this week" value={d.site_visits_this_week} />
        <Kpi label="Virtual consultations" value={d.virtual_consultations} />
        <Kpi label="Leads waiting for reply" value={d.leads_waiting} warn testid="ins-kpi-waiting" />
      </div>
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-2">
        <div className={card} data-testid="ins-pipeline">
          <p className={num}>AED {fmt(d.pipeline?.value_min)}–{fmt(d.pipeline?.value_max)}</p>
          <p className={lbl}>Pipeline value · {d.pipeline?.open_leads} open leads</p>
        </div>
        <div className={card}>
          <p className={num}>AED {fmt(d.revenue_forecast_aed)}</p>
          <p className={lbl}>Revenue forecast (win rate {d.win_rate_pct}%)</p>
        </div>
        <div className={card}>
          <p className={num}>{d.avg_response_seconds}s</p>
          <p className={lbl}>Avg AI response time</p>
        </div>
        <div className={card}>
          <p className={num}>{d.avg_qualification_hours ?? "—"}h</p>
          <p className={lbl}>Avg time to qualify · takeover after {d.avg_takeover_hours ?? "—"}h avg</p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-2">
        <MiniList title="Top services (qualified+)" items={d.top_services}
          render={(s) => `${(s.key || "").replace(/_/g, " ")} — ${s.n}`} />
        <MiniList title="Top salespeople (won)" items={d.top_salespeople} render={(s) => `${s.key} — ${s.n} won`} />
        <MiniList title="Most successful references" items={d.top_references}
          render={(m) => `${m.title} — sent ${m.times_sent}×`} />
        <MiniList title="Most requested styles / materials (concept requests)"
          items={[...(d.top_styles || []).map((s) => ({ ...s, kind: "style" })), ...(d.top_materials || []).map((s) => ({ ...s, kind: "material" }))]}
          render={(s) => `${s.key} (${s.kind}) — ${s.n}`} />
        <MiniList title="Follow-up steps that kept leads engaged" items={d.top_followup_steps}
          render={(f) => `Step ${f.step} — ${f.n} leads`} />
        <MiniList title="Knowledge gaps (unanswered questions)" items={d.knowledge_gaps}
          render={(g) => `"${g.query}"`} testid="ins-gaps" />
      </div>
    </div>
  );
}

function ConversionTab() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { crmApi.insightsConversion().then(setD).catch((e) => setErr(e.message)); }, []);
  if (err) return <p className="text-xs text-[#A3141A]">{err}</p>;
  if (!d) return <p className="text-xs text-[#7A6B5F]">Loading…</p>;
  const t = d.totals || {};
  const funnel = [["Conversations", t.conversations], ["Qualified", t.qualified],
    ["Site visits", t.site_visits_booked], ["Quotes requested", t.quotes_requested],
    ["Quotes sent", t.quotes_sent], ["Won", t.won], ["Lost", t.lost]];
  const Table = ({ title, rows }) => (
    <div className={card}>
      <p className={`${lbl} mb-2`}>{title}</p>
      <table className="w-full text-[11px]">
        <thead><tr className="text-left text-[#7A6B5F]">
          <th className="pb-1"> </th><th>Total</th><th>Qual.</th><th>Won</th><th>Win %</th></tr></thead>
        <tbody>{(rows || []).map((r) => (
          <tr key={r.key} className="border-t border-[#6E1F2C]/8">
            <td className="py-1 text-[#3E2A22]">{String(r.key).replace(/_/g, " ")}</td>
            <td>{r.total}</td><td>{r.qualified}</td><td>{r.won}</td><td>{r.win_rate}%</td>
          </tr>))}
        </tbody>
      </table>
    </div>
  );
  return (
    <div className="space-y-3" data-testid="insights-conversion">
      <div className="grid grid-cols-3 sm:grid-cols-7 gap-2">
        {funnel.map(([k, v]) => <Kpi key={k} label={k} value={v} />)}
      </div>
      <div className={card} data-testid="ins-ai-vs-human">
        <p className={`${lbl} mb-1`}>AI vs Human conversion</p>
        <p className="text-[12px] text-[#3E2A22]">
          AI-handled: {d.ai_vs_human?.ai?.won}/{d.ai_vs_human?.ai?.leads} won ({d.ai_vs_human?.ai?.win_rate}%)
          &nbsp;·&nbsp; Human takeover: {d.ai_vs_human?.human?.won}/{d.ai_vs_human?.human?.leads} won ({d.ai_vs_human?.human?.win_rate}%)
        </p>
      </div>
      <div className="grid xl:grid-cols-3 gap-2">
        <Table title="By service" rows={d.by_service} />
        <Table title="By lead source" rows={d.by_source} />
        <Table title="By salesperson" rows={d.by_salesperson} />
      </div>
    </div>
  );
}

function HealthTab() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => crmApi.insightsHealth().then(setD).catch((e) => setErr(e.message)), []);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);
  if (err) return <p className="text-xs text-[#A3141A]">{err}</p>;
  if (!d) return <p className="text-xs text-[#7A6B5F]">Loading…</p>;
  return (
    <div className="space-y-3" data-testid="insights-health">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <Kpi label="Stuck batches" value={d.stuck_batches} warn testid="ins-health-stuck" />
        <Kpi label="Failed batches (24h)" value={d.failed_batches_24h} warn />
        <Kpi label="Failed sends (24h)" value={d.failed_sends_24h} warn />
        <Kpi label="Failed image gens (24h)" value={d.failed_visuals_24h} warn />
        <div className={card}>
          <p className={num}>{Math.round((d.latency?.avg_ms || 0) / 1000)}s</p>
          <p className={lbl}>Avg conversation latency · {d.latency?.runs_24h} runs (max {Math.round((d.latency?.max_ms || 0) / 1000)}s)</p>
        </div>
      </div>
      <div className={card}>
        <p className={`${lbl} mb-2`}>Errors by type (last hour / 24h) — alerts fire automatically before customers notice</p>
        {(d.errors_by_kind || []).length === 0 && <p className="text-[11px] text-[#3F6E2C]">No errors in the last 24 hours ✓</p>}
        <ul className="space-y-1">
          {(d.errors_by_kind || []).map((e) => (
            <li key={e.kind} className={`text-[11px] ${e.last_hour > 0 ? "text-[#A3141A]" : "text-[#3E2A22]"}`}>
              {e.kind}: {e.last_hour} / {e.last_24h}
            </li>))}
        </ul>
      </div>
      <p className="text-[9px] text-[#7A6B5F]">Checked {d.checked_at?.slice(11, 19)} UTC · auto-refreshes every 20s · watchdog + health alerts run every 10 minutes.</p>
    </div>
  );
}

export default function InsightsPage() {
  const [tab, setTab] = useState("overview");
  return (
    <div className="space-y-4" data-testid="insights-page">
      <PageHead title="AI Sales Insights" sub="Optimization, learning & production health — Phase 5" />
      <div className="flex gap-1.5 flex-wrap">
        {TABS.map(([k, l]) => (
          <button key={k} type="button" data-testid={`insights-tab-${k}`} onClick={() => setTab(k)}
            className={`px-3 py-1.5 text-[10px] tracking-[0.14em] uppercase border ${tab === k
              ? "bg-[#6E1F2C] text-white border-[#6E1F2C]" : "text-[#6E1F2C] border-[#6E1F2C]/30 hover:bg-[#6E1F2C]/5"}`}>
            {l}
          </button>))}
      </div>
      {tab === "overview" && <OverviewTab />}
      {tab === "conversion" && <ConversionTab />}
      {tab === "performance" && <PerformanceTab />}
      {tab === "replay" && <ReplayTab />}
      {tab === "learning" && <LearningTab />}
      {tab === "coaching" && <CoachingTab />}
      {tab === "briefings" && <BriefingsTab />}
      {tab === "sandbox" && <SandboxTab />}
      {tab === "health" && <HealthTab />}
    </div>
  );
}
